import { useCallback, useEffect, useMemo, useState } from 'react'
import { dispatchThemeChange } from '@/components/theme-provider'
import { createLogger } from '@/lib/logger'
import { useAuth } from '@/store/auth.store'

import {
  PreferenceContextEnum,
  PreferenceScopeEnum,
  SidebarTheme,
  ThemePreferences,
} from '@scrambl/shared'

import { useOrganizationStore } from '../store/organization.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '../store/ui-preferences.store'
import { useUserPreferencesStore } from '../store/user-preferences.store'

const logger = createLogger('hooks/useThemePreferences')

export type ThemeMode = 'light' | 'dark' | 'system'

// localStorage key prefixes for instant theme application (prevents flash)
// Color themes are cached per user/org; light/dark mode is local to this user/device.
const THEME_MODE_STORAGE_PREFIX = 'scrambl-ui-theme-user'
const SIDEBAR_THEME_STORAGE_PREFIX = 'scrambl-sidebar-theme-user-org'
const LEGACY_THEME_MODE_STORAGE_PREFIX = 'scrambl-ui-theme'
// Global fallback key (used when no org is selected)
const THEME_MODE_GLOBAL_KEY = 'scrambl-ui-theme-global'
const SIDEBAR_THEME_GLOBAL_KEY = 'scrambl-sidebar-theme-global'

// Default values
const DEFAULT_THEME_MODE: ThemeMode = 'system'
const DEFAULT_SIDEBAR_THEME: SidebarTheme = 'cast-iron-skillet'

// Helper to get org-specific localStorage key
function getThemeModeKey(userId: string | undefined): string {
  return userId
    ? `${THEME_MODE_STORAGE_PREFIX}-${userId}`
    : THEME_MODE_GLOBAL_KEY
}

function getLegacyThemeModeKey(orgId: string | undefined): string {
  return orgId
    ? `${LEGACY_THEME_MODE_STORAGE_PREFIX}-${orgId}`
    : THEME_MODE_GLOBAL_KEY
}

function getSidebarThemeKey(
  userId: string | undefined,
  orgId: string | undefined,
): string {
  return userId && orgId
    ? `${SIDEBAR_THEME_STORAGE_PREFIX}-${userId}-${orgId}`
    : SIDEBAR_THEME_GLOBAL_KEY
}

interface UseThemePreferencesReturn {
  // Current values
  themeMode: ThemeMode
  sidebarTheme: SidebarTheme

  // Computed values
  isDarkMode: boolean
  currentSidebarColor: string

  // Actions
  setThemeMode: (mode: ThemeMode) => Promise<void>
  setSidebarTheme: (theme: SidebarTheme) => Promise<void>

  // Loading state
  isLoading: boolean
}

/**
 * Hook for managing per-user, per-organization theme color preferences.
 *
 * Theme colors are synced at USER scope inside the current organization.
 * Light/dark/system mode is local to this user/device and is not sent to the
 * backend.
 *
 * For instant theme application (to prevent flash on load), values are also
 * cached in localStorage. The color cache is user/org-specific and updated
 * whenever the theme changes or the user switches organizations.
 */
export function useThemePreferences(): UseThemePreferencesReturn {
  const currentOrgId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const currentUserId = useAuth(state => state.user?.id)

  // Store selectors
  const isStoreInitialized = useUserPreferencesStore(
    state => state.isInitialized,
  )
  const storeIsLoading = useUserPreferencesStore(state => state.isLoading)
  const savePreferenceToStore = useUserPreferencesStore(
    state => state.savePreference,
  )
  const storedPreferences = useUserPreferencesStore(state => state.preferences)
  const setUISidebarTheme = useUIPreferencesStore(
    state => state.setSidebarTheme,
  )

  // Optimistic state for immediate UI updates (checkmarks, etc.)
  const [optimisticThemeMode, setOptimisticThemeMode] =
    useState<ThemeMode | null>(null)
  const [optimisticSidebarTheme, setOptimisticSidebarTheme] =
    useState<SidebarTheme | null>(null)

  // Get the user-scoped theme color preference for this organization.
  const rawPreferenceData = useMemo(() => {
    if (!isStoreInitialized) {
      return undefined
    }
    return [...storedPreferences]
      .filter(
        preference =>
          preference.context === PreferenceContextEnum.THEME &&
          preference.scope === PreferenceScopeEnum.USER &&
          (preference.moduleType === null ||
            preference.moduleType === undefined),
      )
      .sort((a, b) => {
        const aUpdated = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
        const bUpdated = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
        if (aUpdated !== bUpdated) return bUpdated - aUpdated
        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return bCreated - aCreated
      })[0]
  }, [isStoreInitialized, storedPreferences])

  // Extract theme values with fallbacks
  const storedThemeMode: ThemeMode = useMemo(() => {
    const stored =
      localStorage.getItem(getThemeModeKey(currentUserId)) ??
      localStorage.getItem(getLegacyThemeModeKey(currentOrgId))
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
    return DEFAULT_THEME_MODE
  }, [currentOrgId, currentUserId])

  const storedSidebarTheme: SidebarTheme = useMemo(() => {
    // If we have a preference from the backend, use it
    if (rawPreferenceData?.preferences) {
      const prefs = rawPreferenceData.preferences as unknown as ThemePreferences
      if (prefs.sidebarTheme) return prefs.sidebarTheme
    }

    if (isStoreInitialized) {
      return DEFAULT_SIDEBAR_THEME
    }

    // Only fall back to localStorage during initial render (before preferences loaded)
    // This prevents flash on page load - use user/org-specific key.
    const stored = localStorage.getItem(
      getSidebarThemeKey(currentUserId, currentOrgId),
    )
    if (stored && SIDEBAR_THEMES.some(t => t.id === stored)) {
      return stored as SidebarTheme
    }
    return DEFAULT_SIDEBAR_THEME
  }, [rawPreferenceData, isStoreInitialized, currentOrgId, currentUserId])

  // Use optimistic values if set, otherwise use stored values
  const themeMode = optimisticThemeMode ?? storedThemeMode
  const sidebarTheme = optimisticSidebarTheme ?? storedSidebarTheme

  // Clear optimistic state when stored values catch up
  useEffect(() => {
    if (
      optimisticThemeMode !== null &&
      storedThemeMode === optimisticThemeMode
    ) {
      setOptimisticThemeMode(null)
    }
  }, [storedThemeMode, optimisticThemeMode])

  useEffect(() => {
    if (
      optimisticSidebarTheme !== null &&
      storedSidebarTheme === optimisticSidebarTheme
    ) {
      setOptimisticSidebarTheme(null)
    }
  }, [storedSidebarTheme, optimisticSidebarTheme])

  // Compute isDarkMode
  const isDarkMode = useMemo(() => {
    if (themeMode === 'dark') return true
    if (themeMode === 'light') return false
    // For 'system', check the media query
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return false
  }, [themeMode])

  // Get current sidebar color based on theme
  const currentSidebarColor = useMemo(() => {
    const themeConfig =
      SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]
    return isDarkMode ? themeConfig.darkColor : themeConfig.lightColor
  }, [sidebarTheme, isDarkMode])

  // Apply theme mode to DOM
  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('dark', 'light')

    if (themeMode === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light'
      root.classList.add(systemTheme)
    } else {
      root.classList.add(themeMode)
    }

    // Cache locally for this user/device only. This value is intentionally not synced.
    localStorage.setItem(getThemeModeKey(currentUserId), themeMode)
    localStorage.setItem(LEGACY_THEME_MODE_STORAGE_PREFIX, themeMode)
  }, [themeMode, currentUserId])

  // Apply sidebar theme to DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-sidebar-theme', sidebarTheme)
    setUISidebarTheme(sidebarTheme)

    // Cache in user/org-specific localStorage for next load
    localStorage.setItem(
      getSidebarThemeKey(currentUserId, currentOrgId),
      sidebarTheme,
    )
  }, [sidebarTheme, currentOrgId, currentUserId, setUISidebarTheme])

  // Listen for system theme changes when mode is 'system'
  useEffect(() => {
    if (themeMode !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      const root = document.documentElement
      root.classList.remove('dark', 'light')
      root.classList.add(mediaQuery.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [themeMode])

  // Save theme mode
  const setThemeMode = useCallback(
    async (mode: ThemeMode) => {
      // Set optimistic state for immediate UI update (checkmarks, etc.)
      setOptimisticThemeMode(mode)

      // Immediately apply to DOM and localStorage. Light/dark mode is device-local.
      const root = document.documentElement
      root.classList.remove('dark', 'light')
      if (mode === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
          .matches
          ? 'dark'
          : 'light'
        root.classList.add(systemTheme)
      } else {
        root.classList.add(mode)
      }
      localStorage.setItem(getThemeModeKey(currentUserId), mode)
      localStorage.setItem(LEGACY_THEME_MODE_STORAGE_PREFIX, mode)

      // Notify ThemeProvider to update its state (for components using useTheme)
      dispatchThemeChange(mode)
    },
    [currentUserId],
  )

  // Save sidebar theme
  const setSidebarTheme = useCallback(
    async (theme: SidebarTheme) => {
      // Set optimistic state for immediate UI update (checkmarks, etc.)
      setOptimisticSidebarTheme(theme)

      // Immediately apply to DOM and org-specific localStorage
      document.documentElement.setAttribute('data-sidebar-theme', theme)
      setUISidebarTheme(theme)
      localStorage.setItem(
        getSidebarThemeKey(currentUserId, currentOrgId),
        theme,
      )

      // Save color theme to backend at user scope so it follows this user across devices
      // inside the current organization without affecting other members.
      const currentPrefs =
        rawPreferenceData?.preferences as unknown as Partial<ThemePreferences>
      const updatedPrefs: Partial<ThemePreferences> = {
        sidebarTheme: theme,
        accentColor: currentPrefs?.accentColor,
        fontSize: currentPrefs?.fontSize,
        density: currentPrefs?.density,
      }

      try {
        await savePreferenceToStore(
          PreferenceContextEnum.THEME,
          updatedPrefs as unknown as Record<string, unknown>,
          undefined,
          PreferenceScopeEnum.USER,
        )
      } catch (error) {
        logger.error('[useThemePreferences] Error saving sidebar theme:', error)
      }
    },
    [
      savePreferenceToStore,
      rawPreferenceData,
      currentOrgId,
      currentUserId,
      setUISidebarTheme,
    ],
  )

  return {
    themeMode,
    sidebarTheme,
    isDarkMode,
    currentSidebarColor,
    setThemeMode,
    setSidebarTheme,
    isLoading: storeIsLoading || !isStoreInitialized,
  }
}
